import { useState, useEffect, useCallback } from 'react';
import { Box, Button, TablePagination } from '@mui/material';
import { toast } from 'react-toastify';
import { useLanguage } from '../../../../context/LanguageContext';
import { useUser } from '../../../../context/UserContext';
import useGetCustomersList from '../../../../hooks/Clients/importList/useGetCustomersList';
import useDeleteCustomersList from '../../../../hooks/Clients/importList/useDeleteCustomersList';
import useGetDetailsCustomersList from '../../../../hooks/Clients/importList/useGetDetailsCustomersList';
import DrawerVisualise from '../../ListImport/components/DrawerVisualise';
import ConfirmDialog from '../../ListImport/components/ConfirmDialog';
import { ImportedList } from '../../ListImport/types';
import ImportedListsTable from './ImportedListsTable';

type ListId = ImportedList['LIST_ID'];

interface PendingDeletion {
    lists: ImportedList[];
    message: string;
    successMessage: string;
}

const isFlagged = (value: ImportedList['IS_EMAIL_LIST']) => (value === '1' ? '1' : '0');

const toDeletePayload = (list: ImportedList) => {
    const flags = {
        is_email_list: isFlagged(list.IS_EMAIL_LIST),
        is_cell_phone_list: isFlagged(list.IS_CELL_PHONE_LIST),
        is_wallet_list: isFlagged(list.IS_WALLET_LIST),
        is_card_list: isFlagged(list.IS_IDENTIFIER_LIST),
    };
    return {
        list_id: list.LIST_ID,
        ...flags,
        is_personid_list: Object.values(flags).includes('1') ? '0' : '1',
    };
};

function MyListsTab() {
    const { t } = useLanguage();
    const { loadingUserInfos } = useUser();
    const { getCustomersList, loading: loadingLists, data: listsData, invalidateListsCache } = useGetCustomersList();
    const { getDetailsCustomersList, loading: loadingDetails, data: detailsData } = useGetDetailsCustomersList();
    const { deleteCustomersList, loading: deleting } = useDeleteCustomersList();

    const [selectedIds, setSelectedIds] = useState<ListId[]>([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerList, setDrawerList] = useState<ImportedList | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const lists: ImportedList[] = listsData?.member || [];
    const totalItems = listsData?.totalItems || 0;

    const loadPage = useCallback(
        () => getCustomersList({ page: page + 1, itemsPerPage: rowsPerPage }),
        [getCustomersList, page, rowsPerPage]
    );

    useEffect(() => {
        loadPage();
    }, [loadPage]);

    const changePage = (newPage: number) => {
        setPage(newPage);
        setSelectedIds([]);
    };

    const handleView = (list: ImportedList) => {
        setDrawerList(list);
        setDrawerOpen(true);
        getDetailsCustomersList({ list_id: list.LIST_ID });
    };

    const toggleRow = (id: ListId) =>
        setSelectedIds(prev => (prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]));

    const toggleAll = () => setSelectedIds(prev => (prev.length > 0 ? [] : lists.map(list => list.LIST_ID)));

    const askDeletion = (deletion: PendingDeletion) => {
        setPendingDeletion(deletion);
        setConfirmOpen(true);
    };

    const askDeleteOne = (list: ImportedList) => askDeletion({
        lists: [list],
        message: t('common.confirmDelete'),
        successMessage: t('common.deleteSuccess'),
    });

    const askDeleteSelection = () => {
        const count = selectedIds.length.toString();
        askDeletion({
            lists: lists.filter(list => selectedIds.includes(list.LIST_ID)),
            message: t('common.confirmDeleteBatch').replace('{count}', count),
            successMessage: t('common.deleteBatchSuccess').replace('{count}', count),
        });
    };

    const confirmDeletion = async () => {
        if (!pendingDeletion) return;
        const { lists: listsToDelete, successMessage } = pendingDeletion;
        setConfirmOpen(false);
        try {
            if (!(await deleteCustomersList(listsToDelete.map(toDeletePayload)))) return;
            toast.success(successMessage);
            invalidateListsCache();
            setSelectedIds(prev => prev.filter(id => !listsToDelete.some(list => list.LIST_ID === id)));
            if (listsToDelete.length === lists.length && page > 0) {
                setPage(page - 1);
            } else {
                loadPage();
            }
        } catch {
            toast.error(t('common.error'));
        }
    };

    return (
        <Box>
            <ImportedListsTable
                lists={lists}
                loading={loadingLists || loadingUserInfos}
                rowsPerPage={rowsPerPage}
                selectedIds={selectedIds}
                onToggleRow={toggleRow}
                onToggleAll={toggleAll}
                onView={handleView}
                onDelete={askDeleteOne}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 2 }}>
                <Button
                    variant="contained"
                    color="error"
                    disabled={selectedIds.length === 0 || deleting}
                    onClick={askDeleteSelection}
                >
                    {t(deleting ? 'common.deleting' : 'common.delete')} {selectedIds.length > 0 && `(${selectedIds.length})`}
                </Button>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalItems}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => changePage(newPage)}
                    onRowsPerPageChange={event => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        changePage(0);
                    }}
                    labelRowsPerPage={t('common.rowsPerPage')}
                    sx={{ marginLeft: 'auto' }}
                />
            </Box>

            <DrawerVisualise
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                data={drawerList}
                detailsData={detailsData}
                loadingDetails={loadingDetails}
            />

            <ConfirmDialog
                open={confirmOpen}
                title={t('common.delete')}
                message={pendingDeletion?.message ?? ''}
                onConfirm={confirmDeletion}
                onCancel={() => setConfirmOpen(false)}
                confirmText={t('common.confirm')}
                cancelText={t('common.cancel')}
                confirmColor="error"
            />
        </Box>
    );
}

export default MyListsTab;
